import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, CheckCircle2, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * SegmentLeadForm
 * ────────────────
 * Whitepaper-style email capture used on the three segment landing
 * pages. Drops leads into the `leads` collection with a `segment` tag
 * so sales can slice HS / College / Pro pipeline separately.
 *
 * Success state pretends there's a gated download waiting — the user
 * can wire that PDF to their marketing DAM later; the intent is that
 * an email is captured and the salesperson has a warm opener.
 */
export default function SegmentLeadForm({
  segment,
  tone = "#22D3EE",
  whitepaperLabel = "Program Intelligence Whitepaper",
  testId = "segment-lead-form",
}) {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", role: "", interest: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, segment });
      setDone(true);
      toast.success("Whitepaper on the way. Check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section
        className="rounded-2xl border p-6 md:p-8"
        style={{
          backgroundColor: "rgba(15,30,54,0.6)",
          borderColor: `${tone}55`,
        }}
        data-testid={`${testId}-success`}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 mt-1 shrink-0" style={{ color: tone }} />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: tone }}>
              You&apos;re on the list
            </div>
            <div className="mt-2 font-display font-black text-2xl md:text-3xl text-white leading-tight">
              Check your inbox for the {whitepaperLabel}.
            </div>
            <p className="mt-3 text-sm text-white/70 max-w-lg">
              Our program-development team will reach out within one business day with your custom onboarding path.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl border p-6 md:p-8"
      style={{
        backgroundColor: "rgba(15,30,54,0.6)",
        borderColor: "rgba(255,255,255,0.10)",
      }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: tone }}>
            <FileDown className="h-3.5 w-3.5" />
            Gated whitepaper · complimentary
          </div>
          <div className="mt-2 font-display font-black text-2xl md:text-3xl text-white leading-tight">
            Get the {whitepaperLabel}.
          </div>
          <p className="mt-3 text-sm text-white/60 max-w-md">
            The exact framework elite programs use to unify movement, workload, recovery, and performance intelligence.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 grid md:grid-cols-2 gap-4" data-testid={`${testId}-fields`}>
        <div>
          <Label className="text-white/70 text-xs">Full name</Label>
          <Input required value={form.name} onChange={upd("name")}
            placeholder="Coach Rodriguez"
            className="mt-1.5 bg-[var(--ink-1)] border-white/15 text-white"
            data-testid={`${testId}-name`}
          />
        </div>
        <div>
          <Label className="text-white/70 text-xs">Work email</Label>
          <Input required type="email" value={form.email} onChange={upd("email")}
            placeholder="you@program.com"
            className="mt-1.5 bg-[var(--ink-1)] border-white/15 text-white"
            data-testid={`${testId}-email`}
          />
        </div>
        <div>
          <Label className="text-white/70 text-xs">Organization</Label>
          <Input required value={form.organization} onChange={upd("organization")}
            placeholder="Your school / program"
            className="mt-1.5 bg-[var(--ink-1)] border-white/15 text-white"
            data-testid={`${testId}-organization`}
          />
        </div>
        <div>
          <Label className="text-white/70 text-xs">Role</Label>
          <Input required value={form.role} onChange={upd("role")}
            placeholder="Head Coach · AT · S&C · Director"
            className="mt-1.5 bg-[var(--ink-1)] border-white/15 text-white"
            data-testid={`${testId}-role`}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-white/70 text-xs">What are you trying to solve? (optional)</Label>
          <Textarea value={form.interest} onChange={upd("interest")}
            placeholder="e.g., unify data across four different vendors"
            className="mt-1.5 bg-[var(--ink-1)] border-white/15 text-white min-h-[70px]"
            data-testid={`${testId}-interest`}
          />
        </div>
        <div className="md:col-span-2">
          <Button
            type="submit"
            disabled={submitting}
            size="lg"
            className="w-full rounded-full py-6 text-base text-white font-bold red-glow"
            style={{ backgroundColor: "var(--brand-secondary)" }}
            data-testid={`${testId}-submit`}
          >
            {submitting ? "Sending…" : "Send me the whitepaper"} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <div className="mt-3 text-[10px] tracking-[0.15em] uppercase text-white/40 text-center">
            No spam · unsubscribe anytime · we don&apos;t sell your data
          </div>
        </div>
      </form>
    </section>
  );
}
